import { API_URLS } from '@helpers/api';
import { withQueryParams } from '@hoc/withQueryParams';
import { useApi } from '@hooks/useApi';
import Pagination from '@stories/Components/Global/Pagination/Pagination';
import queryString from 'query-string';
import React from 'react';
import { ArrayParam, NumberParam, StringParam, useQueryParams, withDefault, } from 'use-query-params';
import { BlogListingStyles as S } from './BlogListing.styles';
import BlogCard from '@stories/Components/Cards/BlogCard/BlogCard';
import { getSvgFromSystem, SystemIcon } from '@helpers/systemIcons';
import Breadcrumb from '../Global/Breadcrumb/Breadcrumb';
import { m } from 'framer-motion';
import { withMotionMax } from '@hoc/withMotionMax';
import BlogFilter from './BlogFilter/BlogFilter';
import FeaturedBlogCard from '@stories/Components/Cards/FeaturedBlogCard/FeaturedBlogCard';
const BlogListing = ({ anchorId, breadcrumbItems, pageContext, initialData, intro, featuredArticle, filter, sortBy, title, translations, }) => {
    const topRef = React.useRef(null);
    const [activeFilters, setActiveFilters] = React.useState(initialData.filter ?? null);
    const [notificationBarHeight, setNotificationBarHeight] = React.useState(0);
    React.useEffect(() => {
        const header = document.getElementById('lp-header');
        if (!header) {
            return;
        }
        const readNotificationHeight = () => {
            const hasNotification = header.getAttribute('data-has-notification-bar') === 'true';
            const notificationBar = header.querySelector('[data-mobile-menu-open]');
            if (!hasNotification || !notificationBar) {
                setNotificationBarHeight(0);
                return;
            }
            setNotificationBarHeight(Math.ceil(notificationBar.getBoundingClientRect().height));
        };
        readNotificationHeight();
        const resizeObserver = new ResizeObserver(readNotificationHeight);
        const notificationBar = header.querySelector('[data-mobile-menu-open]');
        if (notificationBar) {
            resizeObserver.observe(notificationBar);
        }
        const mutationObserver = new MutationObserver(readNotificationHeight);
        mutationObserver.observe(header, {
            attributes: true,
            childList: true,
            subtree: true,
        });
        window.addEventListener('resize', readNotificationHeight);
        return () => {
            resizeObserver.disconnect();
            mutationObserver.disconnect();
            window.removeEventListener('resize', readNotificationHeight);
        };
    }, []);
    // Define query string parameters.
    const [queryParams, setQueryParams] = useQueryParams({
        pageIndex: withDefault(NumberParam, 1),
        searchTerm: withDefault(StringParam, ''),
        filter: withDefault(ArrayParam, initialData.filter ?? []),
        sortBy: withDefault(StringParam, initialData.sortBy ?? sortBy.options[0].value),
    });
    const [tempSearchTerm, setTempSearchTerm] = React.useState(queryParams.searchTerm ?? '');
    // Get page index or default.
    const pageIndex = queryParams.pageIndex;
    const pageSize = 12;
    // Get filter or default.
    const filterValue = queryParams.filter;
    // Get sort by or default.
    const sortByValue = queryParams.sortBy;
    // Construct API url.
    const parameters = queryString.stringify({
        docId: pageContext?.documentId,
        filter: filterValue,
        pageIndex: pageIndex,
        pageSize: pageSize,
        searchTerm: queryParams.searchTerm,
        sortBy: sortByValue,
    });
    const apiUrl = `${API_URLS.ARTICLE_LIST}?${parameters}`;
    // Fetch data.
    const { data } = useApi(apiUrl);
    // Get items from response or initial items.
    const items = data?.items ?? initialData.items;
    const totalItems = data?.totalItems ?? initialData.totalItems;
    const firstNum = items.length < 1 ? 0 : pageSize * pageIndex - (pageSize - 1);
    const secondNum = pageSize * pageIndex - (pageSize - 1) + (items.length - 1);
    React.useEffect(() => {
        if (activeFilters) {
            setQueryParams({ filter: activeFilters });
        }
        else {
            setQueryParams({ filter: [] });
        }
    }, [activeFilters, setQueryParams]);
    return (React.createElement(React.Fragment, null,
        React.createElement(S.BannerArea, { style: {
                '--notificationBarHeight': `${notificationBarHeight}px`,
            } },
            React.createElement(S.AnimationArea, null,
                React.createElement(S.BgAnimation, null,
                    React.createElement(m.svg, { xmlns: "http://www.w3.org/2000/svg", xmlnsXlink: "http://www.w3.org/1999/xlink", viewBox: "0 0 1920 1000" },
                        React.createElement("g", { id: "Group_2093", "data-name": "Group 2093", transform: "translate(1949.289 52) rotate(90)" },
                            React.createElement("g", { id: "Group_2092", "data-name": "Group 2092", transform: "translate(5 25)" },
                                React.createElement(m.path, { initial: { pathLength: 0, pathOffset: 1 }, animate: { pathLength: 1, pathOffset: 0 }, transition: {
                                        duration: 2,
                                        ease: 'easeInOut',
                                        repeat: 0,
                                    }, id: "Path_797", "data-name": "Path 797", d: "M885.187-473.555c30.4,494.971-468.128,87.258-513.12,573.381S937.419,761.6,885.187,1099.642s-693.9,222.7-964.811,135.683", transform: "translate(-61.893 239.699)", fill: "none", stroke: "currentColor", strokeMiterlimit: "10", strokeWidth: "90" }))))),
                React.createElement(S.BgAnimationMobile, null,
                    React.createElement(m.svg, { xmlns: "http://www.w3.org/2000/svg", width: "485.277", height: "642.2", viewBox: "0 0 485.277 642.2" },
                        React.createElement(m.path, { initial: { pathLength: 0, pathOffset: 1 }, animate: { pathLength: 1, pathOffset: 0 }, transition: {
                                duration: 1.5,
                                ease: 'easeInOut',
                                repeat: 0,
                            }, id: "Path_798", "data-name": "Path 798", d: "M-9.928,276.776C-28.416,476.539,402.047,326.131,414.4,514.829s-278.091,98.063-298,399.405", transform: "translate(444.768 916.212) rotate(180)", fill: "none", stroke: "currentColor", "stroke-miterlimit": "10", "stroke-width": "60" })))),
            React.createElement(S.Container, { id: anchorId, flush: 'bottom' },
                React.createElement(S.BannerInner, null,
                    React.createElement(S.BannerSearchArea, null,
                        React.createElement(S.Title, null, title),
                        React.createElement(S.Intro, null, intro),
                        React.createElement("div", { className: 'hide-no-js' },
                            React.createElement(S.SearchInputHolder, null,
                                React.createElement(S.SearchInputLabel, { htmlFor: "siteSearch" }, translations && (translations['translations.blog.search-site'] ?? 'Search for blog articles')),
                                React.createElement(S.SearchInput, { id: "siteSearch", placeholder: translations && (translations['translations.blog.search-placholder'] ?? 'Search articles'), value: tempSearchTerm ? tempSearchTerm : '', onChange: (event) => setTempSearchTerm(event.target.value), onKeyDown: (event) => {
                                        if (event.key !== 'Enter') {
                                            return;
                                        }
                                        setQueryParams({ searchTerm: tempSearchTerm, pageIndex: 1 }, 'pushIn');
                                    } }),
                                React.createElement(S.SearchSubmit, { onClick: () => setQueryParams({ searchTerm: tempSearchTerm, pageIndex: 1 }, 'pushIn') }, getSvgFromSystem(SystemIcon.Search))))),
                    React.createElement("div", { className: 'hide-no-js' },
                        React.createElement(S.FilterArea, null,
                            React.createElement(BlogFilter, { ...filter, activeBlogFilter: activeFilters, translations: translations, firstNum: firstNum, secondNum: secondNum, totalItems: totalItems, sortBy: {
                                    labelText: sortBy.label,
                                    options: sortBy.options,
                                    value: sortByValue,
                                }, onSortBy: (val) => setQueryParams({ sortBy: val, pageIndex: 1 }), onFilterSelect: (filterValue) => {
                                    let filters = activeFilters;
                                    if (filters && activeFilters) {
                                        if (filters.includes(filterValue)) {
                                            filters = activeFilters.filter((x) => x !== filterValue);
                                        }
                                        else {
                                            filters = [...activeFilters, filterValue];
                                        }
                                        initialData.filter = [];
                                        setActiveFilters(filters);
                                    }
                                    else {
                                        setActiveFilters([filterValue]);
                                    }
                                }, onFilterClear: () => {
                                    initialData.filter = [];
                                    setActiveFilters(null);
                                } }))))),
            breadcrumbItems && React.createElement(Breadcrumb, { items: breadcrumbItems.items })),
        React.createElement(S.ListingContainer, { flush: 'top' },
            featuredArticle &&
                React.createElement(S.FeaturedArea, null,
                    React.createElement(FeaturedBlogCard, { ...featuredArticle })),
            React.createElement(S.Listing, null, items && items.map((item) => React.createElement(BlogCard, { key: `item-${item.id}`, ...item, translations: translations }))),
            React.createElement(S.Bottom, null,
                React.createElement(S.PaginationContainer, null, items.length < totalItems && (React.createElement(Pagination, { pageIndex: pageIndex, pageSize: pageSize, onPageChange: (pageIndex) => {
                        setQueryParams({ pageIndex });
                        topRef.current?.scrollIntoView();
                    }, totalCount: totalItems }))),
                React.createElement(S.ListingTotals, null,
                    React.createElement(S.ListingTotalsText, null,
                        translations && (translations['translations.blog.showing'] ?? 'Showing'),
                        " ",
                        firstNum,
                        " -",
                        ' ',
                        secondNum,
                        " ",
                        translations && (translations['translations.blog.of'] ?? 'of'),
                        " ",
                        totalItems,
                        ' ',
                        translations && (translations['translations.blog.results'] ?? 'Articles')))))));
};
export default withMotionMax(withQueryParams(BlogListing));
